import { useState } from "react";
import type { ReactNode } from "react";
import { ApiError } from "../lib/http";
import { login } from "../lib/auth";
import { ui } from "../ui";
import { BrandLogo } from "./layout";

export function LoginScreen({
  onSignedIn,
}: {
  onSignedIn: () => void;
}): ReactNode {
  const [identifier, setIdentifier] = useState("");
  const [password, setPassword] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const submit = async (event: React.FormEvent): Promise<void> => {
    event.preventDefault();
    setBusy(true);
    setError(null);
    try {
      await login(identifier.trim(), password);
      // First sign-in needs one online moment to seed the offline roster.
      // Imported dynamically so Dexie stays out of the signed-out bundle.
      try {
        const { pullSync } = await import("../lib/sync");
        await pullSync();
      } catch {
        // Non-fatal: the clock-in screen retries the pull on mount.
      }
      onSignedIn();
    } catch (cause) {
      setError(
        cause instanceof ApiError ? cause.message : "Could not sign in",
      );
    } finally {
      setBusy(false);
    }
  };

  return (
    <section className="mx-auto flex min-h-[80vh] max-w-sm flex-col items-center justify-center gap-6">
      <div className="flex justify-center">
        <BrandLogo size="lg" />
      </div>
      <div className={`${ui.card} w-full space-y-4 p-6`}>
        <div className="space-y-1 text-center">
          <h2 className="text-xl font-bold text-ink">Sign in</h2>
          <p className="text-xs text-muted">
            Sign in once while online — the app works offline afterwards.
          </p>
        </div>
        <form onSubmit={(e) => void submit(e)} className="space-y-3">
          <label className="block space-y-1">
            <span className={ui.label}>Email or mobile</span>
            <input
              type="text"
              required
              autoComplete="username"
              placeholder="you@example.com or +9715…"
              value={identifier}
              onChange={(e) => setIdentifier(e.target.value)}
              className={ui.input}
            />
          </label>
          <label className="block space-y-1">
            <span className={ui.label}>Password</span>
            <input
              type="password"
              required
              autoComplete="current-password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              className={ui.input}
            />
          </label>
          {error !== null ? (
            <p className="text-xs font-medium text-absent">{error}</p>
          ) : null}
          <button
            type="submit"
            disabled={busy}
            className={`${ui.btn} ${ui.primary} w-full py-4 text-base`}
          >
            {busy ? "Signing in…" : "Sign in"}
          </button>
        </form>
      </div>
    </section>
  );
}
